import { useCallback, useMemo } from 'react'
import { Send } from 'lucide-react'
import ActiveMark from '../../components/ActiveMark'
import FormRow from '../../components/FormRow'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { FormCols, FormSection, FormSpan, SwitchField } from '../../components/Form'
import MailProfilesPanel from './MailProfilesPanel'
import {
  CONNECTION_METHODS,
  deleteSmtpProfile,
  exportSmtpProfiles,
  importSmtpProfiles,
  listSmtpProfiles,
  saveSmtpProfile,
  testSmtpProfile,
  SMTP_SORTABLE,
} from './phishingService'
import { phishingSettingsStrings as S } from './strings'

const F = S.smtpForm

const BLANK = {
  id: 0,
  settingName: '',
  name: '',
  from: '',
  visibleSenderAddress: '',
  host: '',
  port: '587',
  username: '',
  password: '',
  connectionMethod: 'StartTls',
  validateServerCertificate: '',
  isActive: true,
}

/**
 * إعدادات ارسال البريد — the sender profiles a phishing campaign sends from.
 *
 * A profile is three decisions, and the form is grouped that way: WHO the message appears to
 * come from, WHICH server carries it, and how that server is spoken to. The legacy modal is
 * one flat run of eleven controls where the envelope address sits between two display names.
 *
 * TWO ADDRESSES, deliberately. `From` is the SMTP envelope sender — the address the server
 * authorises the message against — while the visible sender is what the recipient reads.
 * A campaign that spoofs a domain sets them apart; everything else leaves the visible one
 * empty and both become `From`.
 */
export default function SmtpProfilesPanel() {
  /* Stable identity: the panel reloads whenever `list` changes. */
  const list = useCallback((options) => listSmtpProfiles(options), [])

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: S.smtp.senderName,
        render: (row) => (
          <span className="ph__stack">
            <bdi>{row.name}</bdi>
            {row.settingName && <small className="ph__muted">{row.settingName}</small>}
          </span>
        ),
      },
      { key: 'from', header: S.smtp.from, render: (row) => <span dir="ltr">{row.from}</span> },
      {
        key: 'host',
        header: S.smtp.host,
        value: (row) => `${row.host}:${row.port}`,
        render: (row) => (
          <span dir="ltr">
            {row.host}:{row.port}
          </span>
        ),
      },
      {
        key: 'isActive',
        header: S.smtp.status,
        align: 'center',
        value: (row) => (row.isActive ? S.active : S.inactive),
        render: (row) => (
          <ActiveMark active={Boolean(row.isActive)} label={row.isActive ? S.active : S.inactive} />
        ),
      },
    ],
    [],
  )

  const validate = (values) => {
    const found = {}
    if (!values.name.trim()) found.name = S.required
    if (!values.from.trim()) found.from = S.required
    if (!values.host.trim()) found.host = S.required
    if (!String(values.port).trim()) found.port = S.required
    if (!values.connectionMethod) found.connectionMethod = S.required
    return found
  }

  return (
    <MailProfilesPanel
      stateKey="phishing-smtp"
      icon={Send}
      columns={columns}
      blank={BLANK}
      labels={{
        add: F.add,
        edit: F.edit,
        subtitle: S.tabs.smtp,
        empty: F.empty,
        deleteConfirm: F.deleteConfirm,
        searchPlaceholder: S.smtp.senderName,
        imported: F.imported,
        saved: F.saved,
        deleted: F.deleted,
        tested: F.tested,
        fail: F.fail,
      }}
      list={list}
      sortable={SMTP_SORTABLE}
      defaultSort={{ key: 'name', dir: 'asc' }}
      save={saveSmtpProfile}
      remove={deleteSmtpProfile}
      validate={validate}
      test={{
        label: F.test,
        prompt: { title: F.test, label: F.testAddress, placeholder: 'name@example.com' },
        run: (values, address) => testSmtpProfile(values, address),
      }}
      transfer={{
        onExport: exportSmtpProfiles,
        onImport: importSmtpProfiles,
        note: F.importNote,
      }}
    >
      {({ values, set, errors }) => (
        <>
          {/* Who the message comes from. */}
          <FormSection>
            <FormCols>
              <FormRow label={F.name} error={errors.name} hint={F.nameHint}>
                {(id) => (
                  <TextField
                    id={id}
                    value={values.name}
                    onChange={set('name')}
                    invalid={!!errors.name}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow label={F.settingName} hint={F.settingNameHint}>
                {(id) => (
                  <TextField
                    id={id}
                    value={values.settingName}
                    onChange={set('settingName')}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow label={F.from} error={errors.from} hint={F.fromHint}>
                {(id) => (
                  <TextField
                    id={id}
                    dir="ltr"
                    value={values.from}
                    onChange={set('from')}
                    invalid={!!errors.from}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow label={F.visibleSenderAddress} hint={F.visibleSenderAddressHint}>
                {(id) => (
                  <TextField
                    id={id}
                    dir="ltr"
                    value={values.visibleSenderAddress}
                    onChange={set('visibleSenderAddress')}
                    autoComplete="off"
                  />
                )}
              </FormRow>
            </FormCols>
          </FormSection>

          {/* Which server carries it, and how it is signed in to. */}
          <FormSection>
            <FormCols>
              <FormRow label={F.host} error={errors.host} hint={F.hostHint}>
                {(id) => (
                  <TextField
                    id={id}
                    dir="ltr"
                    value={values.host}
                    onChange={set('host')}
                    invalid={!!errors.host}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow label={F.port} error={errors.port} hint={F.portHint}>
                {(id) => (
                  <TextField
                    id={id}
                    dir="ltr"
                    type="number"
                    value={values.port}
                    onChange={set('port')}
                    invalid={!!errors.port}
                  />
                )}
              </FormRow>

              <FormRow label={F.username} hint={F.usernameHint}>
                {(id) => (
                  <TextField
                    id={id}
                    dir="ltr"
                    value={values.username}
                    onChange={set('username')}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow
                label={F.password}
                hint={values.id ? F.passwordKeptHint : F.passwordHint}
              >
                {(id) => (
                  <TextField
                    id={id}
                    dir="ltr"
                    type="password"
                    value={values.password}
                    onChange={set('password')}
                    autoComplete="new-password"
                  />
                )}
              </FormRow>
            </FormCols>
          </FormSection>

          <FormSection>
            <FormCols>
              <FormRow
                label={F.connectionMethod}
                error={errors.connectionMethod}
                hint={F.connectionMethodHint}
              >
                {(id) => (
                  <Select
                    id={id}
                    value={values.connectionMethod}
                    onChange={set('connectionMethod')}
                    options={CONNECTION_METHODS}
                    invalid={!!errors.connectionMethod}
                  />
                )}
              </FormRow>

              {/* Three-valued: left unset the server's own default applies. */}
              <FormRow label={F.validateCertificate} hint={F.validateCertificateHint}>
                {(id) => (
                  <Select
                    id={id}
                    value={values.validateServerCertificate}
                    onChange={set('validateServerCertificate')}
                    options={[
                      { value: 'true', label: F.certificateValidate },
                      { value: 'false', label: F.certificateSkip },
                    ]}
                    placeholder={F.certificateDefault}
                  />
                )}
              </FormRow>

              <FormSpan>
                <SwitchField
                  checked={values.isActive}
                  onChange={set('isActive')}
                  label={F.active}
                  hint={F.activeHint}
                />
              </FormSpan>
            </FormCols>
          </FormSection>
        </>
      )}
    </MailProfilesPanel>
  )
}
